import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Phone, Smartphone, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, toast } from '@/App';
import { logClientError } from '@/utils/clientError';
import { buildAvailableMobileMoneyMethods } from '@/utils/paymentMethodAvailability';

const MAX_PROOF_SIZE_BYTES = 8 * 1024 * 1024;
const ALLOWED_PROOF_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

const MobileMoneyPayment = ({
  amount,
  currency,
  onPaymentMethodSelect,
  selectedMethod,
  transactionId = '',
  onTransactionIdChange,
  receiptFile = null,
  onReceiptFileChange,
}) => {
  const [settings, setSettings] = useState({
    payment_numbers: {
      airtel: '',
      mtn: '',
      orange: '',
      mpesa: '',
    },
    payment_methods: {},
  });
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    const loadPaymentNumbers = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data || {});
      } catch (error) {
        logClientError('mobile_money.load_settings', error);
      }
    };

    loadPaymentNumbers();
  }, []);

  const paymentOptions = useMemo(() => buildAvailableMobileMoneyMethods(settings), [settings]);
  const hasAvailableOptions = paymentOptions.length > 0;
  const selectedOption = paymentOptions.find((item) => item.id === selectedMethod) || null;

  useEffect(() => {
    if (!hasAvailableOptions && selectedMethod) {
      onPaymentMethodSelect('');
    }
  }, [hasAvailableOptions, onPaymentMethodSelect, selectedMethod]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setFileError('');
      onReceiptFileChange?.(null);
      return;
    }

    if (!ALLOWED_PROOF_TYPES.has(String(file.type || '').toLowerCase())) {
      setFileError('Formats acceptes : JPG, JPEG ou PNG.');
      onReceiptFileChange?.(null);
      toast.error('Formats acceptes : JPG, JPEG ou PNG.');
      return;
    }
    if (file.size > MAX_PROOF_SIZE_BYTES) {
      setFileError('Le fichier ne doit pas depasser 8 MB.');
      onReceiptFileChange?.(null);
      toast.error('Le fichier ne doit pas depasser 8 MB.');
      return;
    }

    setFileError('');
    onReceiptFileChange?.(file);
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-cyan-200 bg-cyan-50">
        <CardContent className="p-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Montant a payer</p>
            <p className="text-3xl font-bold text-cyan-700">
              {Number(amount || 0).toLocaleString()} {currency}
            </p>
          </div>
        </CardContent>
      </Card>

      <div>
        <Label className="text-lg font-semibold mb-3 block">
          <Smartphone className="w-5 h-5 inline mr-2" />
          Choisissez votre operateur Mobile Money
        </Label>
        {!hasAvailableOptions && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Aucune methode Mobile Money n&apos;est disponible pour le moment.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {paymentOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onPaymentMethodSelect(option.id)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                selectedMethod === option.id
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 hover:border-red-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-lg">{option.name}</p>
                  <p className="text-sm text-gray-600 mt-1">Numero: {option.number}</p>
                </div>
                {selectedMethod === option.id && (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    OK
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedOption && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Instructions de paiement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-amber-200">
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>
                  Envoyez <strong className="text-cyan-700">{Number(amount || 0).toLocaleString()} {currency}</strong> au numero <strong>{selectedOption.number}</strong>.
                </li>
                <li>Conservez la capture ou le recu de votre paiement.</li>
                <li>Ajoutez la preuve ci-dessous pour soumettre votre demande.</li>
                <li>Notre equipe validera ensuite le paiement depuis l&apos;admin.</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="mobile-money-transaction-id">Reference de transaction (optionnel)</Label>
              <Input
                id="mobile-money-transaction-id"
                placeholder="Ex: TXN123456789"
                value={transactionId}
                onChange={(event) => onTransactionIdChange?.(event.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si votre operateur fournit un identifiant de transaction, ajoutez-le pour accelerer la verification.
              </p>
            </div>

            <div>
              <Label htmlFor="mobile-money-proof-upload">Preuve de paiement *</Label>
              <div className="mt-1">
                <label
                  htmlFor="mobile-money-proof-upload"
                  className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {receiptFile ? receiptFile.name : 'Cliquez pour televerser votre preuve'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">JPG, JPEG ou PNG - 8 MB max</p>
                  </div>
                  <input
                    id="mobile-money-proof-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
              {fileError && (
                <p className="text-xs text-red-600 mt-2">{fileError}</p>
              )}
              {!fileError && receiptFile && (
                <p className="text-xs text-green-700 mt-2">Preuve selectionnee: {receiptFile.name}</p>
              )}
            </div>

            <Card className="bg-gradient-to-r from-red-50 to-cyan-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Validation admin requise</p>
                    <p className="text-sm text-gray-700">
                      Votre demande restera en attente de validation apres l&apos;upload de la preuve. Aucun debit n&apos;est considere comme valide tant que l&apos;admin n&apos;a pas confirme le paiement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MobileMoneyPayment;
export { MobileMoneyPayment };
